
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PuttingResult } from "@/types/puttingResults";
import { format } from "date-fns";
import { enUS, fr, es } from "date-fns/locale";
import { Badge } from "@/components/ui/badge";
import { Search, Download } from "lucide-react";
import { getTranslatedTestName } from "@/utils/trainingResults/translationUtils";

interface TrainingResultsTableProps {
  results: PuttingResult[];
  isCoach: boolean;
}

const TrainingResultsTable: React.FC<TrainingResultsTableProps> = ({ results, isCoach }) => {
  const { t, i18n } = useTranslation('training');
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<"test_date" | "points" | "test_name">("test_date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Get locale for date formatting
  const getLocale = () => {
    switch (i18n.language) {
      case 'fr': return fr;
      case 'es': return es;
      default: return enUS;
    }
  };

  // Filtrer les résultats selon le terme de recherche
  const filteredResults = results.filter(result => 
    (result.test_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (result.test_id?.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (result.test_level?.toString().includes(searchTerm))
  );

  // Trier les résultats
  const sortedResults = [...filteredResults].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortField) {
      case "test_date":
        aValue = new Date(a.test_date).getTime();
        bValue = new Date(b.test_date).getTime();
        break;
      case "points":
        aValue = a.points;
        bValue = b.points;
        break;
      case "test_name":
        aValue = a.test_name || a.test_id || "";
        bValue = b.test_name || b.test_id || "";
        break;
      default:
        return 0;
    }
    
    if (sortDirection === "asc") {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const exportToCSV = () => {
    const headers = ["Date", "Exercice", "Niveau", "Points"];
    const csvData = sortedResults.map(result => [
      format(new Date(result.test_date), i18n.language === 'en' ? 'MM/dd/yyyy' : 'dd/MM/yyyy', { locale: getLocale() }),
      result.test_name || result.test_id || "Standard",
      result.test_level || "N/A",
      result.points.toString()
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `resultats_entrainement_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getLevelColor = (level: string | null) => {
    switch (level) {
      case "1": return "bg-green-100 text-green-800";
      case "2": return "bg-blue-100 text-blue-800";
      case "3": return "bg-purple-100 text-purple-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getPointsColor = (points: number) => {
    if (points >= 12) return "text-green-600 font-semibold";
    if (points >= 8) return "text-blue-600 font-medium";
    if (points >= 4) return "text-orange-600";
    return "text-red-600";
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>{t('trainingResults.table.title')} ({sortedResults.length})</CardTitle>
          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder={t('trainingResults.table.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
            {isCoach && (
              <Button onClick={exportToCSV} variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                {t('trainingResults.table.export')}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort("test_date")}
                >
                  {t('trainingResults.table.date')} {sortField === "test_date" && (sortDirection === "asc" ? "↑" : "↓")}
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort("test_name")}
                >
                  {t('trainingResults.table.exercise')} {sortField === "test_name" && (sortDirection === "asc" ? "↑" : "↓")}
                </TableHead>
                <TableHead>{t('trainingResults.table.level')}</TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort("points")}
                >
                  {t('trainingResults.table.points')} {sortField === "points" && (sortDirection === "asc" ? "↑" : "↓")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedResults.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                    {t('trainingResults.table.noResults')}
                  </TableCell>
                </TableRow>
              ) : (
                sortedResults.map((result) => (
                  <TableRow key={result.id} className="hover:bg-gray-50">
                    <TableCell>
                      {format(new Date(result.test_date), i18n.language === 'en' ? 'MM/dd/yyyy \'at\' HH:mm' : 'dd/MM/yyyy \'à\' HH:mm', { locale: getLocale() })}
                    </TableCell>
                    <TableCell className="font-medium">
                      {getTranslatedTestName(result.test_id || "", result.test_level, t)}
                    </TableCell>
                    <TableCell>
                      <Badge className={getLevelColor(result.test_level)}>
                        {t('trainingResults.table.levelLabel')} {result.test_level || "N/A"}
                      </Badge>
                    </TableCell>
                    <TableCell className={getPointsColor(result.points)}>
                      {result.points} pts
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default TrainingResultsTable;
